import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";

import {
  ChevronDown,
  Menu,
  X,
  Navigation, // Consistent icon used in all dropdown links
} from "lucide-react";

// --- Link Data ---
const aboutLinks = [
  { to: "/who-we-are", label: "Who We Are", icon: Navigation },
  { to: "/our-corexperts", label: "Our CoreXperts", icon: Navigation },
  { to: "/our-interns", label: "Our Interns", icon: Navigation },
];

const solutionLinks = [
  { to: "/our-services", label: "Our Services", icon: Navigation },
];

const innovationLinks = [
  { to: "/our-products", label: "Our Products", icon: Navigation },
];

// ✅ Updated: Added Portfolio under Insights
const insightLinks = [
  { to: "/blogs", label: "Blogs", icon: Navigation },
];
// -----------------

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  // Ref to track the entire navbar element
  const navRef = useRef(null);

  /**
   * Toggles the state of a dropdown menu based on its unique key.
   * If the clicked menu is already open, it closes it (sets to null).
   * Otherwise, it opens the clicked menu.
   */
  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const location = useLocation();

  const handleNavClick = (path) => (e) => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);

    if (location.pathname === path) {
      e.preventDefault();          // stop react-router
      window.location.reload();    // 🔥 hard refresh
    }
  };



  /**
   * Effect hook to listen for clicks outside the navigation bar.
   * If an open dropdown exists and the click target is outside the navbar, close the dropdown.
   */
  useEffect(() => {
    function handleClickOutside(event) {
      // If a dropdown is open AND the click target is outside the main nav component, close the dropdown.
      if (openDropdown !== null && navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const DropdownMenu = ({ links }) => (
    // Desktop dropdown positioned absolutely
    <div className="absolute left-0 mt-2 w-60 bg-white shadow-xl rounded-lg border border-gray-100 overflow-hidden">
      {links.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="flex items-center gap-4 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition no-underline border-b border-gray-100 last:border-b-0"
          // Close all menus upon clicking a link inside
          onClick={handleNavClick(item.to)}
        >
          <item.icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span>{item.label}</span>
        </Link>
      ))}
    </div>
  );

  return (
    // Attach the ref to the main nav element
    <nav ref={navRef} className="w-full bg-white shadow-sm sticky top-0 z-[200]">
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 no-underline"
          onClick={() => {
            setOpenDropdown(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img
            src="/img/LOGO.png"
            alt="SecurXpert Logo"
            className="h-8 md:h-10"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src =
                "https://placehold.co/180x40/FFFFFF/212529?text=Logo+Error";
            }}
          />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8 text-[15px] font-medium">
          <Link
            to="/"
            onClick={(e) => {
              setOpenDropdown(null);

              if (location.pathname === "/") {
                e.preventDefault();        // stop React Router
                window.location.reload(); // 🔥 HARD refresh
              }
            }}
            className="text-gray-700 hover:text-blue-600 transition no-underline"
          >
            Home
          </Link>

          {/* About Us (Click-to-Open) */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown("about")}
              className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition bg-transparent border-0 cursor-pointer focus:outline-none"
              aria-expanded={openDropdown === "about"}
              aria-controls="about-menu"
            >
              About Us
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "about" ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            {openDropdown === "about" && <DropdownMenu links={aboutLinks} />}
          </div>

          {/* Solutions (Click-to-Open) */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown("solutions")}
              className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition bg-transparent border-0 cursor-pointer focus:outline-none"
              aria-expanded={openDropdown === "solutions"}
              aria-controls="solutions-menu"
            >
              Solutions
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "solutions" ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            {openDropdown === "solutions" && (
              <DropdownMenu links={solutionLinks} />
            )}
          </div>

          {/* Innovations (Click-to-Open) */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown("innovations")}
              className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition bg-transparent border-0 cursor-pointer focus:outline-none"
              aria-expanded={openDropdown === "innovations"}
              aria-controls="innovations-menu"
            >
              Innovations
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "innovations"
                    ? "rotate-180 text-blue-600"
                    : ""
                  }`}
              />
            </button>
            {openDropdown === "innovations" && (
              <DropdownMenu links={innovationLinks} />
            )}
          </div>

          {/* Insights (Click-to-Open) */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown("insights")}
              className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition bg-transparent border-0 cursor-pointer focus:outline-none"
              aria-expanded={openDropdown === "insights"}
              aria-controls="insights-menu"
            >
              Insights
              <ChevronDown
                className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "insights" ? "rotate-180 text-blue-600" : ""
                  }`}
              />
            </button>
            {openDropdown === "insights" && <DropdownMenu links={insightLinks} />}
          </div>

          <Link
            to="/careers"
            onClick={(e) => {
              setOpenDropdown(null);

              if (location.pathname === "/careers") {
                e.preventDefault();        // stop React Router
                window.location.reload(); // 🔥 HARD refresh
              }
            }}
            className="text-gray-700 hover:text-blue-600 transition no-underline"
          >
            Xperts-Life
          </Link>
          <Link
            to="/contact-us"
            onClick={(e) => {
              setOpenDropdown(null);

              if (location.pathname === "/contact-us") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }

            }}
            className="text-gray-700 hover:text-blue-600 transition no-underline"
          >
            Contact Us
          </Link>

        </div>

        {/* Request Quote Button */}
        <div className="hidden lg:block">
          <Link
            to="/get-quoted"
            onClick={() => setOpenDropdown(null)} // Explicitly close when navigating
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-full text-sm font-semibold shadow-md hover:shadow-lg hover:from-purple-800 hover:to-purple-700 transition-all duration-300 no-underline"
          >
            Request A Quote
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-gray-800 bg-transparent border-0 cursor-pointer focus:outline-none"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-white z-40 overflow-y-auto animate-slide-in">
          <div className="flex justify-between items-center p-3 border-b border-gray-200">
            {/* Logo in Mobile Drawer */}
            <Link
              to="/"
              className="flex items-center gap-2 no-underline"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img
                src="/img/LOGO.png"
                alt="SecurXpert Logo"
                className="h-8"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/180x40/FFFFFF/212529?text=Logo+Error";
                }}
              />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-transparent border-0 cursor-pointer focus:outline-none"
              aria-label="Close mobile menu"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
          </div>

          <div className="flex flex-col p-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition no-underline"
            >
              Home
            </Link>

            {/* About Us Mobile (Click-to-Open) */}
            <div>
              <button
                onClick={() => toggleDropdown("about-mobile")}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition bg-transparent border-0 cursor-pointer text-left focus:outline-none"
              >
                <span>About Us</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "about-mobile" ? "rotate-180" : ""
                    }`}
                />
              </button>
              {openDropdown === "about-mobile" && (
                <div className="ml-4 mt-1 space-y-1">
                  {aboutLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick(item.to)}
                      className="flex items-center gap-4 px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition no-underline"
                    >
                      <item.icon className="w-4 h-4 text-blue-600" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Solutions Mobile (Click-to-Open) */}
            <div>
              <button
                onClick={() => toggleDropdown("solutions-mobile")}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition bg-transparent border-0 cursor-pointer text-left focus:outline-none"
              >
                <span>Solutions</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "solutions-mobile" ? "rotate-180" : ""
                    }`}
                />
              </button>
              {openDropdown === "solutions-mobile" && (
                <div className="ml-4 mt-1 space-y-1">
                  {solutionLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick(item.to)}
                      className="flex items-center gap-4 px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition no-underline"
                    >
                      <item.icon className="w-4 h-4 text-blue-600" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Innovations Mobile (Click-to-Open) */}
            <div>
              <button
                onClick={() => toggleDropdown("innovations-mobile")}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition bg-transparent border-0 cursor-pointer text-left focus:outline-none"
              >
                <span>Innovations</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "innovations-mobile" ? "rotate-180" : ""
                    }`}
                />
              </button>
              {openDropdown === "innovations-mobile" && (
                <div className="ml-4 mt-1 space-y-1">
                  {innovationLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick(item.to)}
                      className="flex items-center gap-4 px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition no-underline"
                    >
                      <item.icon className="w-4 h-4 text-blue-600" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Insights Mobile (Click-to-Open) */}
            <div>
              <button
                onClick={() => toggleDropdown("insights-mobile")}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition bg-transparent border-0 cursor-pointer text-left focus:outline-none"
              >
                <span>Insights</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${openDropdown === "insights-mobile" ? "rotate-180" : ""
                    }`}
                />
              </button>
              {openDropdown === "insights-mobile" && (
                <div className="ml-4 mt-1 space-y-1">
                  {insightLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={handleNavClick(item.to)}
                      className="flex items-center gap-4 px-4 py-3 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition no-underline"
                    >
                      <item.icon className="w-4 h-4 text-blue-600" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/careers"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition no-underline"
            >
              Xperts-Life
            </Link>
            <Link
              to="/contact-us"
              onClick={() => setIsMobileMenuOpen(false)}
              className="px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition no-underline"
            >
              Contact Us
            </Link>
           <Link
  to="/get-quoted"
  onClick={() => setIsMobileMenuOpen(false)}
  className="inline-block bg-[#031552ff] text-white px-6 py-3 mt-4 rounded-full font-semibold no-underline transition-all duration-300 hover:bg-[#2d1164]"
>
  Request A Quote
</Link>


          </div>
        </div>
      )}

      {/* Animations and Base Styles */}
      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        a, button {
          text-decoration: none !important;
        }
        button {
          font-family: inherit;
          font-size: inherit;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
